import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createClasses,
  Tab,
  Tabs,
  TransparentButton,
} from "@bloomberg/media-ui-core";
import { Change, Variant } from "@bloomberg/media-ui-markets";
import { ScreenContext } from "@foundation/screen";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import type { Watchlist } from "@foundation/watchlist-client";
import styles from "./styles.module.css";
import type {
  HeatmapTicker,
  HeatmapTile,
  HeatmapTileData,
  HeatmapTileTrend,
} from "~types/types";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { getChangeSeverity } from "~components/Heatmap/util";
import Heatmap from "~components/Heatmap";
import { formatValue } from "~utils/section-front/formatChange";
import { getCurrencySymbol } from "~utils/portfolio/getCurrencySymbol";
import WatchlistDashboardTitle from "~components/WatchlistDashboardTitle";
import HeatmapLegend from "~components/WatchlistHeatmap/HeatmapLegend";
import { DASHBOARD_v2_FEEDBACK_URL } from "~constants/Portfolio";
import {
  formatDecimals,
  formatPriceLabel,
} from "~utils/portfolio/formatNumber";
import useLink from "~hooks/useLink";

const MemoizedHeatmapLegend = memo(HeatmapLegend);

interface WatchlistHeatmapProps {
  width?: number;
  height?: number;
}

const LOCAL_STORAGE_KEY = "wlHeatmapTab";

const SORT_BY_TAB_PERFORMANCE = "Performance";
const SORT_BY_TAB_HOLDINGS_SIZE = "Holdings Size";

const SORT_BY_PERFORMANCE = "performance";
const SORT_BY_HOLDINGS = "holdings";

const LARGE_DESKTOP_CHART_MAXIMIZED_HEIGHT = 600;
const SMALL_DESKTOP_CHART_MAXIMIZED_HEIGHT = 600;
const TABLET_CHART_MAXIMIZED_HEIGHT = 600;
const MOBILE_CHART_MAXIMIZED_HEIGHT = 360;
const MOBILE_CHART_MINIMIZED_HEIGHT = 333;
const BASE_CHART_WIDTH = 500;
const BASE_CHART_HEIGHT = 280;

// a function which takes the breakpoint and maximization state as input and returns the
// width and height of the chart as output
function getChartDimensions(
  tablet: boolean | null,
  smallDesktop: boolean | null,
  largeDesktop: boolean | null,
  isMaximized: boolean
) {
  if (isMaximized) {
    if (largeDesktop) {
      return {
        chartWidth: BASE_CHART_WIDTH,
        chartHeight: LARGE_DESKTOP_CHART_MAXIMIZED_HEIGHT,
      };
    } else if (smallDesktop) {
      return {
        chartWidth: BASE_CHART_WIDTH,
        chartHeight: SMALL_DESKTOP_CHART_MAXIMIZED_HEIGHT,
      };
    } else if (tablet) {
      return {
        chartWidth: BASE_CHART_WIDTH,
        chartHeight: TABLET_CHART_MAXIMIZED_HEIGHT,
      };
    }

    return {
      chartWidth: BASE_CHART_WIDTH,
      chartHeight: MOBILE_CHART_MAXIMIZED_HEIGHT,
    };
  }

  // minimized dimensions
  if (largeDesktop || smallDesktop || tablet) {
    return {
      chartWidth: BASE_CHART_WIDTH,
      chartHeight: BASE_CHART_HEIGHT,
    };
  }

  // mobile dimensions
  return {
    chartWidth: BASE_CHART_WIDTH,
    chartHeight: MOBILE_CHART_MINIMIZED_HEIGHT,
  };
}

function prepareHeatmapTiles(
  selectedPortfolio: Watchlist,
  sortBy: "performance" | "holdings"
) {
  let lotsAvailable = false;
  const positions: HeatmapTicker[] = selectedPortfolio.positions.map(
    (position) => {
      // market value of current ticker position, computed based on the
      // shares purchased and current ticker price
      const marketValue =
        position.shares?.number > 0 && position.marketData?.PX_LAST
          ? position.shares.number * position.marketData?.PX_LAST
          : 0;

      // watchlist is assumed to have user entered lots,
      // when a position has non-zero number of shares
      if (position.shares?.number > 0) {
        lotsAvailable = true;
      }

      return {
        ticker: position.security.ticker,
        tickerMarketValue: marketValue,
        marketData: position.marketData,
        marketValue: position.marketValue,
        security: position.security,
        shares: position.shares,
      } as HeatmapTicker;
    }
  );

  // assign default market value for watchlist without user entered lots
  if (!lotsAvailable) {
    // eslint-disable-next-line no-param-reassign
    positions.forEach((position) => (position.tickerMarketValue = 1));
  }

  // sort tickers by market value
  let sortedPositions: HeatmapTicker[];

  // sort tickers based on user choice
  if (sortBy === SORT_BY_PERFORMANCE) {
    // one day percent change
    sortedPositions = positions.sort(
      (a, b) =>
        (b.marketData?.CHG_PCT_1D || 0) - (a.marketData?.CHG_PCT_1D || 0)
    );
  } else {
    // number of shares X current price
    sortedPositions = positions.sort(
      (a, b) => b.tickerMarketValue - a.tickerMarketValue
    );
  }

  // size of the tile is always based on market value (shares X price), the sort by doesn't impact this
  const totalSize = sortedPositions.reduce(
    (sum, ticker) => sum + ticker.tickerMarketValue,
    0
  );

  const getTrend = (percentChange: number): HeatmapTileTrend => {
    if (percentChange > 0) return "positive";
    if (percentChange < 0) return "negative";

    return "neutral";
  };

  // prepare tile data with color, label and size information
  const tileData: Array<HeatmapTileData<HeatmapTicker>> = sortedPositions.map(
    (tickerData) => {
      const percentChange = tickerData.marketData?.CHG_PCT_1D || 0;

      const severity = getChangeSeverity(percentChange);

      const percentChangeContent = (
        <div className={styles.percentChange}>
          {formatPriceLabel(percentChange)}
          <span className={styles.percentSymbol}>%</span>
        </div>
      );

      return {
        ...tickerData,
        severity,
        label: tickerData.ticker,
        content: percentChangeContent,
        trend: getTrend(percentChange),
        size:
          totalSize > 0
            ? Number(
                formatDecimals(
                  (tickerData.tickerMarketValue / totalSize) * 100,
                  2
                )
              )
            : 0,
      };
    }
  );

  return tileData;
}

export default function WatchlistHeatmap(props: WatchlistHeatmapProps) {
  // select tab based on user's previous choice
  const userSelectedHeatmapTab =
    localStorage.getItem(LOCAL_STORAGE_KEY) || SORT_BY_TAB_PERFORMANCE;

  const [isMaximized, setIsMaximized] = useState(false);

  const {
    breakpoints: { tablet, smallDesktop, largeDesktop },
  } = useContext(ScreenContext);

  // get chart dimensions according to breakpoint
  const { chartWidth, chartHeight } = useMemo(() => {
    return getChartDimensions(tablet, smallDesktop, largeDesktop, isMaximized);
  }, [tablet, smallDesktop, largeDesktop, isMaximized]);

  const { width: rawWidth = chartWidth, height = chartHeight } = props;

  const { dashboardView, selectedPortfolio, updateDashboardView } =
    usePortfolioPage();

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [width, setWidth] = useState(rawWidth);

  const [sortBy, setSortBy] = useState<"performance" | "holdings">(
    SORT_BY_PERFORMANCE
  );
  const [selectedTab, setSelectedTab] = useState(userSelectedHeatmapTab);

  const link = useLink();

  const trackHeatmapTabClick = useCallback((tabTitle: string) => {
    const eventLabel =
      tabTitle === SORT_BY_TAB_HOLDINGS_SIZE
        ? "holdings_size_view"
        : "performance_view";

    trackClickEvent(`watchlistHeatmap`, eventLabel, "click", "watchlist");

    // update selected tab
    setSelectedTab(tabTitle);

    // remember user's selection
    localStorage.setItem(LOCAL_STORAGE_KEY, tabTitle);
  }, []);

  useEffect(() => {
    if (selectedTab === SORT_BY_TAB_HOLDINGS_SIZE) {
      setSortBy(SORT_BY_HOLDINGS);
    } else {
      setSortBy(SORT_BY_PERFORMANCE);
    }
  }, [selectedTab]);

  const heatmapTilesData = useMemo(() => {
    // skip rendering when positions are not available
    if (!selectedPortfolio?.positions?.length) {
      return [];
    }

    return prepareHeatmapTiles(selectedPortfolio, sortBy);
  }, [selectedPortfolio, sortBy]);

  /**
   * handle window/container resizing
   */
  useEffect(() => {
    if (!containerRef.current) return undefined;

    let widthObserver: ResizeObserver;
    const observed = containerRef?.current;

    if (observed) {
      widthObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const nextWidth =
            (entry.contentBoxSize[0] as ResizeObserverSize).inlineSize ||
            entry.contentRect.width ||
            width;

          setWidth(nextWidth);
        }
      });

      widthObserver.observe(observed);
    }

    return () => {
      if (widthObserver && observed) {
        widthObserver.unobserve(observed);
      }
    };
  }, [containerRef, width]);

  useEffect(() => {
    // update maximized state, when dashboard view is updated
    // to handle updates from external components
    if (dashboardView === "HTMP") {
      setIsMaximized(true);
    } else {
      setIsMaximized(false);
    }
  }, [dashboardView]);

  const onSeeMoreOrLessClick = () => {
    if (isMaximized) {
      trackClickEvent(
        "watchlistHide",
        "minimize_view_heatmap",
        "click",
        "watchlist"
      );

      // update maximized state
      setIsMaximized(false);

      // reset dashboard view to default
      updateDashboardView("DASH");
    } else {
      trackClickEvent(
        "watchlistShow",
        "expand_view_heatmap",
        "click",
        "watchlist"
      );

      // update maximized state
      setIsMaximized(true);

      // set dashboard view as heatmap
      updateDashboardView("HTMP");
    }
  };

  const onTickerTileClick = (tile: HeatmapTile<HeatmapTicker>) => {
    trackClickEvent(
      "watchlistHeatmap",
      "click_tile_heatmap",
      "click",
      "watchlist"
    );

    // open quotes page on click, ignore mobile clicks
    if (tablet || smallDesktop || largeDesktop) {
      link.open(`/quote/${tile.data.label}`, "_blank");
    }
  };

  const onTickerTileBlur = useCallback(
    (tile: HeatmapTile<HeatmapTicker>) => {
      const hoveredTicker = tile.data.label;
      const maxTicker = heatmapTilesData[0].label;
      const minTicker = heatmapTilesData[heatmapTilesData.length - 1].label;

      if (
        (hoveredTicker === maxTicker || hoveredTicker === minTicker) &&
        sortBy === "performance"
      ) {
        trackClickEvent(
          "chartHover",
          "hover_heatmap_largest_delta",
          "uiaction",
          "Market Data"
        );
      }
      if (hoveredTicker === maxTicker && sortBy === "holdings") {
        trackClickEvent(
          "chartHover",
          "hover_heatmap_largest_holding",
          "uiaction",
          "Market Data"
        );
      }
    },
    [heatmapTilesData, sortBy]
  );

  const renderHoverContent = (tile: HeatmapTile<HeatmapTicker>) => {
    return (
      <>
        <div
          className={createClasses(styles.hoverContent, styles.tickerSymbol)}
        >
          {tile.data.ticker}
        </div>

        <div
          className={createClasses(
            styles.hoverContent,
            tile.data.shares?.number >= 1
              ? styles.tickerSymbol
              : styles.tickerName
          )}
        >
          {tile.data.security.LONG_COMP_NAME}
        </div>

        <div className={styles.hoverContent}>
          {getCurrencySymbol(tile.data.marketData?.CRNCY || "")}{" "}
          {formatValue(tile.data.marketData?.PX_LAST)}
        </div>

        {/* render change when market data is available */}
        {tile.data.marketData?.CHG_PCT_1D &&
          tile.data.marketData?.CHG_NET_1D && (
            <div className={styles.hoverContent}>
              <Change
                name="change"
                percentChange={tile.data.marketData?.CHG_PCT_1D}
                priceChange={tile.data.marketData?.CHG_NET_1D}
                size="large"
                variant={Variant.refresh2024}
              />
            </div>
          )}

        {/* render shares only when lot is available */}
        {tile.data.shares?.number > 0 && (
          <div className={styles.hoverContent}>
            {formatValue(tile.data.shares?.number, 2, 0)}{" "}
            {tile.data.shares?.number > 1 ? "Shares" : "Share"}
          </div>
        )}
      </>
    );
  };

  const cx = createClasses([
    styles.heatmapContainer,
    {
      [styles.maximized]: isMaximized,
    },
  ]);

  return (
    <div ref={containerRef} className={cx}>
      <WatchlistDashboardTitle
        title="1D Heatmap"
        iconVariant="beta"
        description="Sort the heatmap by holdings or performance to track your watchlist’s gains and losses at a glance."
        feedbackUrl={DASHBOARD_v2_FEEDBACK_URL}
        feedbackTrackingEventLabel="survey_heatmap"
        tooltipOpenEventLabel="tooltip_heatmap_open"
        tooltipCloseEventLabel="tooltip_heatmap_close"
      >
        <TransparentButton
          size="small"
          className={styles.actionButton}
          onClick={onSeeMoreOrLessClick}
        >
          {isMaximized ? "See Less" : "See More"}
        </TransparentButton>
      </WatchlistDashboardTitle>
      <div className={styles.tabsContainer}>
        <Tabs
          className={styles.tabs}
          size={largeDesktop ? "default" : "small"}
          variant="gliding"
          onTabSwitch={trackHeatmapTabClick}
        >
          {[SORT_BY_TAB_PERFORMANCE, SORT_BY_TAB_HOLDINGS_SIZE].map(
            (chartType) => (
              <Tab
                key={chartType}
                title={chartType}
                isActive={selectedTab === chartType}
                isDefault={selectedTab === chartType}
              >
                <></>
              </Tab>
            )
          )}
        </Tabs>
        {isMaximized && <MemoizedHeatmapLegend />}
      </div>
      <Heatmap<HeatmapTicker>
        width={width}
        height={height}
        tilesData={heatmapTilesData}
        enableTileFocusEvents
        onTileClick={onTickerTileClick}
        onTileBlur={onTickerTileBlur}
        renderHoverContent={renderHoverContent}
      />
    </div>
  );
}
